import "../../assets/ContentEditor.css";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ExplainedButton as Button } from '../explained-button';
import { useManagedModuleEditor } from "../../providers/managed-module-management-provider";
import { FragmentNode } from "./editor-fragment-node";
import { ActionModal } from "./actions-dialog";
import { ManagedFragmentDto, FragmentActionDto } from "../../general/dtos";

export function FlowCanvas() {
  const {
    fragments,
    updateFragmentsOrder,
    selectedFragmentKey,
    selectFragment,
    addAction,
    updateAction,
    removeAction,
    updateActionsOrder,
    validationTarget
  } = useManagedModuleEditor();

  // --- Hoisted Modal State ---
  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
    fragment: ManagedFragmentDto | null;
    action?: FragmentActionDto;
  }>({
    isOpen: false,
    mode: 'add',
    fragment: null
  });

  useEffect(() => {
    const issue = validationTarget?.issue;
    if (issue?.scope !== 'fragment' || issue.field !== 'actions') return;
    const fragment = fragments.find(f => f.key === issue.fragmentKey);
    if (fragment) setModalConfig({ isOpen: true, mode: 'add', fragment });
    // A checklist request opens the dialog once, rather than on every fragment edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationTarget]);

  /**
   * Memoize the sorted fragments list.
   * This prevents the list from re-sorting unless the fragments array 
   * reference actually changes (e.g., after an add/delete/reorder).
   */
  const sortedFragments = useMemo(() => {
    return [...(fragments ?? [])].sort((a, b) => a.order - b.order);
  }, [fragments]);

  // --- Modal Handlers ---
  const handleOpenAdd = useCallback((fragment: ManagedFragmentDto) => {
    setModalConfig({ isOpen: true, mode: 'add', fragment });
  }, []);

  const handleOpenEdit = useCallback((fragment: ManagedFragmentDto, action: FragmentActionDto) => {
    setModalConfig({ isOpen: true, mode: 'edit', fragment, action });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleSaveAction = useCallback((action: FragmentActionDto) => {
    if (!modalConfig.fragment) return;

    if (modalConfig.mode === 'add') {
      addAction(modalConfig.fragment.key, action);
    } else {
      updateAction(modalConfig.fragment.key, action);
    }
    handleCloseModal();
  }, [modalConfig, addAction, updateAction, handleCloseModal]);

  // --- Reordering Logic ---
  const moveUp = (fragmentKey: string) => {
    const idx = sortedFragments.findIndex(f => f.key === fragmentKey);
    if (idx <= 0) return;

    const next = [...sortedFragments];
    const tmp = next[idx - 1];
    next[idx - 1] = next[idx];
    next[idx] = tmp;

    next.forEach((f, i) => (f.order = i));
    updateFragmentsOrder(next);
  };

  const moveDown = (fragmentKey: string) => {
    const idx = sortedFragments.findIndex(f => f.key === fragmentKey);
    if (idx === -1 || idx >= sortedFragments.length - 1) return;

    const next = [...sortedFragments];
    const tmp = next[idx + 1];
    next[idx + 1] = next[idx];
    next[idx] = tmp;

    next.forEach((f, i) => (f.order = i));
    updateFragmentsOrder(next);
  };

  return (
    <div 
      className="flow-canvas surface-card p-3 border-round flex flex-column gap-3" 
      style={{ overflowY: 'auto' }}
    >
      {sortedFragments.map((fragment, index) => {
        const isFirst = index === 0;
        const isLast = index === sortedFragments.length - 1;

        return (
          <div key={fragment.key} className="flex align-items-start gap-2">
            <div style={{ flex: 1 }}>
              <FragmentNode 
                fragment={fragment} 
                isSelected={selectedFragmentKey === fragment.key}
                onSelect={selectFragment}
                // Updated triggers for hoisted modal
                onAddAction={handleOpenAdd}
                onEditAction={handleOpenEdit}
                removeAction={removeAction}
                updateActionsOrder={updateActionsOrder}
                allFragments={fragments}
              />
            </div>
            
            <div className="flex flex-column gap-1">
              <Button 
                disabled={isFirst} 
                disabledReason="This is already the first fragment."
                tooltip="Move fragment up" 
                icon="pi pi-chevron-up" 
                className="p-button-text p-button-sm" 
                onClick={() => moveUp(fragment.key)} 
              />
              <Button 
                disabled={isLast} 
                disabledReason="This is already the last fragment."
                tooltip="Move fragment down" 
                icon="pi pi-chevron-down" 
                className="p-button-text p-button-sm" 
                onClick={() => moveDown(fragment.key)} 
              />
            </div>
          </div>
        );
      })}

      {/* SINGLE MODAL INSTANCE 
          Hoisting this out of the map ensures we only ever have 
          one ActionModal in the DOM at a time.
      */}
      {modalConfig.isOpen && modalConfig.fragment && (
        <ActionModal
          mode={modalConfig.mode}
          fragment={modalConfig.fragment}
          action={modalConfig.action}
          onSave={handleSaveAction}
          onCancel={handleCloseModal}
        />
      )}
    </div>
  );
}
